// FirmwareComments — a compact, collapsible comment thread that lives on each
// firmware card. Click the bubble to expand; the thread loads lazily on first
// open (the card already knows the count from the list payload, so the badge
// shows without fetching). Append-only: post, never edit/delete — matching every
// other message thread in the app.
//
// We deliberately do NOT reuse the shared thread component here: it renders a
// single fixed floating bubble on mobile, so one-per-card would collide. We reuse
// its data contract instead (same message shape + endpoint convention).
//
// firmware_messages is the LAST legacy per-document message table still in use
// (event/tour/airspace moved to the universal `messages` table in migrations
// 024-026). Migrating it needs this collision solved first, plus the comment-count
// badge on the Download cards and Flerpie's firmware_messages prompt entry.

import React, { useState, useCallback, useEffect } from 'react';
import { buttonClass } from '../components/ui/Button';
import Field from '../components/ui/Field';
import { Panel } from '../components/ui/Panel';
import Reveal from '../components/ui/Reveal';
import FadeIn from '../components/ui/FadeIn';
import EmptyState from '../components/ui/EmptyState';
import { SkeletonGroup, SkeletonText } from '../components/ui/Skeleton';
import dayjs from 'dayjs';
import { useToast } from '../components/Toast/ToastContext';
import { useFirmwareApi } from './firmwareApi';
import type { FirmwareMessage } from './types';

interface Props {
  firmwareId: number;
  count: number; // initial count from the list payload (badge before expand)
  defaultOpen?: boolean; // detail page opens the thread expanded
}

const ChatIcon: React.FC = () => (
  <svg width="15" height="15" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
  </svg>
);

const FirmwareComments: React.FC<Props> = ({ firmwareId, count, defaultOpen = false }) => {
  const api = useFirmwareApi();
  const toast = useToast();
  const [open, setOpen] = useState(defaultOpen);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState<FirmwareMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);

  // Once we've loaded the thread, its length is the source of truth (so a new
  // post bumps the badge); before that, trust the count from the list payload.
  const localCount = loaded ? messages.length : count;
  const panelId = `fw-comments-${firmwareId}`;

  const loadOnce = useCallback(async () => {
    if (loaded || loading) return;
    setLoading(true);
    try {
      const res = await api.listMessages(firmwareId);
      setMessages(res || []);
      setLoaded(true);
    } catch {
      toast.error('Failed to load comments');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firmwareId, loaded, loading]);

  // When mounted already-open (detail page), load the thread immediately.
  useEffect(() => { if (open) loadOnce(); }, [open, loadOnce]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) loadOnce();
  };

  const send = async (e: React.FormEvent) => {
    e.preventDefault();
    const text = draft.trim();
    if (!text || sending) return;
    setSending(true);
    try {
      const msg = await api.postMessage(firmwareId, text);
      setMessages((prev) => [...prev, msg]);
      setDraft('');
    } catch {
      toast.error('Failed to post comment');
    } finally {
      setSending(false);
    }
  };

  const authorName = (m: FirmwareMessage) => `${m.firstname || ''} ${m.lastname || ''}`.trim() || 'Unknown';

  return (
    <div>
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        aria-controls={panelId}
        className={`inline-flex items-center gap-1.5 text-xs transition-colors ${open ? 'text-primary' : 'text-on-surface-variant hover:text-on-surface'}`}
      >
        <ChatIcon />
        <span>{localCount > 0 ? `${localCount} comment${localCount === 1 ? '' : 's'}` : 'Comment'}</span>
      </button>

      <Reveal show={open}>
        <Panel id={panelId} label="Comments" padding="sm" className="mt-2.5">
          {loading ? (
            <SkeletonGroup label="Loading comments" className="mb-2.5"><SkeletonText lines={2} /></SkeletonGroup>
          ) : (
            <FadeIn className="space-y-2 max-h-56 overflow-y-auto mb-2.5">
              {messages.length === 0 && (
                <EmptyState variant="line" dense>No comments yet — start the thread.</EmptyState>
              )}
              {messages.map((m) => (
                <div key={m.id} className="text-sm">
                  <div className="flex items-baseline gap-2">
                    <span className="font-semibold text-on-surface text-xs">{authorName(m)}</span>
                    <span className="text-[10px] text-on-surface-variant">{dayjs(m.created_at).format('MMM D, h:mm A')}</span>
                  </div>
                  <p className="text-on-surface whitespace-pre-wrap break-words text-[13px]">{m.message_text}</p>
                </div>
              ))}
            </FadeIn>
          )}
          <form onSubmit={send} className="flex gap-2">
            <Field
              type="text"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="Add a comment…"
              maxLength={5000}
              aria-label="Add a comment"
              className="flex-1"
            />
            <button
              type="submit"
              disabled={!draft.trim() || sending}
              className={`${buttonClass('quiet', 'md')} flex-shrink-0`}
            >
              {sending ? '…' : 'Post'}
            </button>
          </form>
        </Panel>
      </Reveal>
    </div>
  );
};

export default FirmwareComments;
